/**
 * OAuth callback handler that verifies sessions and populates auth store.
 * Handles post-login redirect including pending invite token processing.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

/**
 * OAuth callback handler
 *
 * This component handles the redirect from OAuth providers after successful authentication.
 * The backend sets HTTP-only cookies before redirecting here, so we need to:
 * 1. Verify the session exists (cookies are sent automatically)
 * 2. Fetch user profile with brands
 * 3. Populate auth store
 * 4. Redirect to dashboard
 */
export function Callback() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { setSession, setUser, setTeams, setAccounts, setActiveTeam } = useAuthStore();
	const [error, setError] = useState<string | null>(null);

	// Check for error in query params
	const errorParam = searchParams.get("error");
	const success = searchParams.get("success") === "true";

	// Fetch session from cookies
	const sessionQuery = trpc.auth.getSession.useQuery(undefined, {
		enabled: success && !errorParam,
		retry: false,
	});

	// Fetch user profile when session is available
	const meQuery = trpc.auth.me.useQuery(undefined, {
		enabled: !!sessionQuery.data,
		retry: false,
	});

	useEffect(() => {
		// Handle error from OAuth provider
		if (errorParam) {
			setError(`Authentication failed: ${errorParam.replace(/_/g, " ")}`);
			return;
		}

		// Handle missing success parameter
		if (!success) {
			setError("Invalid callback - missing success parameter");
			return;
		}

		// Handle session fetch error
		if (sessionQuery.error) {
			setError("Failed to verify session. Please try logging in again.");
			return;
		}

		// Handle missing session (cookies not set or expired)
		// Note: sessionQuery.data being null means query completed with no session
		if (sessionQuery.data === null) {
			setError("Session not found. Please try logging in again.");
			return;
		}

		// Handle user profile fetch error
		if (meQuery.error) {
			setError("Failed to load user profile. Please try logging in again.");
			return;
		}

		// When both session and user data are available, populate store and redirect
		if (sessionQuery.data && meQuery.data) {
			// Set session
			setSession(sessionQuery.data);

			// Set user (without brands and accounts fields which come from me query)
			const { brands, accounts, ...user } = meQuery.data;
			setUser(user);

			// Set teams and accounts lists
			setTeams(brands);
			setAccounts(accounts);

			// Set active brand (first one if available)
			if (brands.length > 0 && brands[0]) {
				setActiveTeam(brands[0]);
			}

			// Check for pending invite token (stored before redirecting to OAuth)
			const pendingInviteToken = sessionStorage.getItem("pendingInviteToken");
			if (pendingInviteToken) {
				// Redirect to invite acceptance page with token
				void navigate(`/invite?token=${pendingInviteToken}`, { replace: true });
			}
			else {
				// Navigate to the default landing resolver
				void navigate("/", { replace: true });
			}
		}
	}, [
		errorParam,
		success,
		sessionQuery.data,
		sessionQuery.error,
		sessionQuery.isLoading,
		meQuery.data,
		meQuery.error,
		setSession,
		setUser,
		setTeams,
		setAccounts,
		setActiveTeam,
		navigate,
	]);

	// Show error state
	if (error) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-8 shadow-lg text-center">
					<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
						<svg
							className="h-6 w-6 text-red-400"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
					<h2 className="text-xl font-semibold">Authentication Error</h2>
					<p className="text-sm text-muted-foreground">{error}</p>
					<button
						onClick={() => navigate("/login", { replace: true })}
						className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
					>
						Back to Login
					</button>
				</div>
			</div>
		);
	}

	// Show loading state
	return (
		<div className="flex min-h-screen items-center justify-center bg-background">
			<div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-8 shadow-lg text-center">
				<div className="mx-auto flex h-12 w-12 items-center justify-center">
					<svg
						className="h-8 w-8 animate-spin text-primary"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
				</div>
				<h2 className="text-xl font-semibold">Completing sign in...</h2>
				<p className="text-sm text-muted-foreground">
					Please wait while we verify your credentials
				</p>
			</div>
		</div>
	);
}
